import React, { useState, useEffect } from "react";
import { Modal, Form, Space, Checkbox, Alert, notification, Descriptions, theme, Switch } from "antd";
import { NumberOutlined, DeploymentUnitOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import QuantityInputMethod from "./QuantityInputMethod";
import { QuantityInputMethod as QuantityInputMethodEnum } from "../../../models/order-product/types";
import { IProduct } from "../../../models/product/response";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import { IStore } from "../../../models/store/response";
import StoreSelector from "./StoreSelector";

interface OrderProduct {
    id?: number;
    product: IProduct;
    count?: number;
    unitPartition?: {
        id: number;
        name: string;
        percentage: string | number;
    };
    store?: IStore;
    store_id?: number;
    availableWeight?: number;
    isAccepted?: boolean;
}

interface Props {
    visible: boolean;
    orderId: number;
    orderProduct: OrderProduct | null;
    onCancel: () => void;
    onSuccess: () => void;
}

const EditProductModal: React.FC<Props> = ({
    visible,
    orderId,
    orderProduct,
    onCancel,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [markAsAccepted, setMarkAsAccepted] = useState(true);
    const [storeId, setStoreId] = useState<number | undefined>(undefined);
    const [quantityData, setQuantityData] = useState<{
        method: QuantityInputMethodEnum;
        count?: number;
        unitPartitionId?: number;
        availableWeight?: number;
    }>({
        method: QuantityInputMethodEnum.BY_COUNT,
        count: 1,
    });
    const [orderProductService] = useState(
        () => new OrderProductService(httpclient)
    );

    useEffect(() => {
        if (visible && orderProduct) {
            initializeForm();
        }
    }, [visible, orderProduct]);

    const initializeForm = () => {
        if (!orderProduct) return;

        // Initialize store from product data
        const productStoreId = orderProduct.store_id || orderProduct.store?.id;
        setStoreId(productStoreId);

        // Initialize with current values to avoid false-positive "changed" state
        const currentMethod = orderProduct.availableWeight !== undefined && orderProduct.availableWeight !== null
            ? QuantityInputMethodEnum.BY_WEIGHT
            : QuantityInputMethodEnum.BY_COUNT;
        const initialData = {
            method: currentMethod,
            count: currentMethod === QuantityInputMethodEnum.BY_COUNT ? orderProduct.count : undefined,
            unitPartitionId: currentMethod === QuantityInputMethodEnum.BY_COUNT ? orderProduct.unitPartition?.id : undefined,
            availableWeight: currentMethod === QuantityInputMethodEnum.BY_WEIGHT ? orderProduct.availableWeight : undefined,
        };

        setQuantityData(initialData);
        setMarkAsAccepted(orderProduct.isAccepted !== false);
    };

    const resetForm = () => {
        form.resetFields();
        setMarkAsAccepted(true);
        setStoreId(undefined);
        setQuantityData({
            method: QuantityInputMethodEnum.BY_COUNT,
        });
    };

    const getStoreQuantityInfo = () => {
        if (!orderProduct?.product?.storeQuantities || !orderProduct?.store?.id) {
            return null;
        }

        const storeQty = orderProduct.product.storeQuantities.find(
            (sq) => sq.store_id === orderProduct.store.id
        );

        if (!storeQty) {
            return null;
        }

        const factor = orderProduct.product.unit.factor_accounting_system || 1;
        const availableUnits = storeQty.quantity / factor;

        return {
            available: storeQty.available,
            quantity: availableUnits,
            storeName: storeQty.store_name,
        };
    };

    const hasChanges = (): boolean => {
        if (!orderProduct) return false;

        // Check if "mark as accepted" changed from current value
        const currentAcceptedStatus = orderProduct.isAccepted !== false;
        if (markAsAccepted !== currentAcceptedStatus) {
            return true;
        }

        // Determine current method from orderProduct
        const currentMethod = orderProduct.availableWeight !== undefined && orderProduct.availableWeight !== null
            ? QuantityInputMethodEnum.BY_WEIGHT
            : QuantityInputMethodEnum.BY_COUNT;

        // Check if method changed
        if (quantityData.method !== currentMethod) {
            return true;
        }

        // Check if values changed based on method
        if (quantityData.method === QuantityInputMethodEnum.BY_COUNT) {
            // Check if count or unitPartition changed
            if (quantityData.count !== orderProduct.count) return true;
            if (quantityData.unitPartitionId !== orderProduct.unitPartition?.id) return true;
        }

        if (quantityData.method === QuantityInputMethodEnum.BY_WEIGHT) {
            // Check if weight changed
            if (quantityData.availableWeight !== orderProduct.availableWeight) return true;
        }

        return false;
    };

    const validateForm = (): boolean => {
        const hasExistingStore = !!(orderProduct?.store_id || orderProduct?.store?.id);
        if (!hasExistingStore && !storeId) {
            notification.error({
                message: t("validation_error"),
                description: t("please_select_store"),
            });
            return false;
        }

        if (!hasChanges()) {
            notification.warning({
                message: t("no_changes"),
                description: t("no_changes_detected"),
            });
            return false;
        }

        const { method, count, availableWeight } = quantityData;

        if (method === QuantityInputMethodEnum.BY_COUNT) {
            if (!count || count < 1) {
                notification.error({
                    message: t("validation_error"),
                    description: t("count_must_be_at_least_1"),
                });
                return false;
            }
        }

        if (method === QuantityInputMethodEnum.BY_WEIGHT) {
            if (availableWeight === undefined || availableWeight === null || availableWeight <= 0) {
                notification.error({
                    message: t("validation_error"),
                    description: t("weight_must_be_greater_than_zero"),
                });
                return false;
            }
        }

        return true;
    };

    const handleMethodChange = (checked: boolean) => {
        const newMethod = checked ? QuantityInputMethodEnum.BY_WEIGHT : QuantityInputMethodEnum.BY_COUNT;
        setQuantityData({
            method: newMethod,
            count: newMethod === QuantityInputMethodEnum.BY_COUNT ? quantityData.count : undefined,
            unitPartitionId: newMethod === QuantityInputMethodEnum.BY_COUNT ? quantityData.unitPartitionId : undefined,
            availableWeight: newMethod === QuantityInputMethodEnum.BY_WEIGHT ? quantityData.availableWeight : undefined,
        });
    };

    const handleSubmit = async () => {
        if (!validateForm() || !orderProduct?.id) return;

        setLoading(true);

        try {
            const currentMethod = orderProduct.availableWeight !== undefined && orderProduct.availableWeight !== null
                ? QuantityInputMethodEnum.BY_WEIGHT
                : QuantityInputMethodEnum.BY_COUNT;

            const quantityChanged = quantityData.method !== currentMethod
                || (quantityData.method === QuantityInputMethodEnum.BY_COUNT
                    && (quantityData.count !== orderProduct.count
                        || quantityData.unitPartitionId !== orderProduct.unitPartition?.id))
                || (quantityData.method === QuantityInputMethodEnum.BY_WEIGHT
                    && quantityData.availableWeight !== orderProduct.availableWeight);

            const updateData: any = {
                productOrderId: orderProduct.id,
                // Backend validation expects this field on every update request.
                isAccepted: markAsAccepted,
            };

            if (quantityChanged && markAsAccepted) {
                if (quantityData.method === QuantityInputMethodEnum.BY_COUNT) {
                    updateData.count = quantityData.count;
                    if (quantityData.unitPartitionId !== undefined) {
                        updateData.unitPartitionId = quantityData.unitPartitionId;
                    }
                } else {
                    updateData.availableWeight = quantityData.availableWeight;
                }
            }

            if (!(orderProduct?.store_id || orderProduct?.store?.id) && storeId) {
                updateData.storeId = storeId;
            }

            await orderProductService.updateProduct(updateData);

            notification.success({
                message: t("success"),
                description: t("product_updated_successfully"),
            });

            onSuccess();
            resetForm();
        } catch (error: any) {
            console.error("Error updating product:", error);

            if (error?.response?.status === 422) {
                const errors = error.response.data?.errors || {};
                const errorMessages = Object.values(errors).flat();
                notification.error({
                    message: t("validation_error"),
                    description: errorMessages.join(", ") || t("please_check_form"),
                });
            } else {
                notification.error({
                    message: t("error"),
                    description: error?.message || t("failed_to_update_product"),
                });
            }
        } finally {
            setLoading(false);
        }
    };

    if (!orderProduct) return null;

    return (
        <Modal
            title={t("edit_product")}
            open={visible}
            onCancel={onCancel}
            width="90%"
            style={{ maxWidth: 700 }}
            onOk={handleSubmit}
            okText={t("save_changes")}
            cancelText={t("cancel")}
            confirmLoading={loading}
            aria-labelledby="edit-product-modal-title"
            aria-describedby="edit-product-modal-description"
            okButtonProps={{ "aria-label": t("save_changes") } as any}
            cancelButtonProps={{ "aria-label": t("cancel") } as any}
        >
            <Form form={form} layout="vertical">
                <div
                    style={{
                        padding: "20px",
                        backgroundColor: token.colorBgContainer,
                        borderRadius: token.borderRadiusLG,
                        border: `1px solid ${token.colorBorder}`,
                        marginBottom: 16,
                    }}
                >
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 12px",
                            backgroundColor: token.colorFillQuaternary,
                            borderRadius: token.borderRadius
                        }}>
                            <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>
                                {t("product_name")}
                            </span>
                            <span style={{ fontWeight: 600, color: token.colorText }}>
                                {orderProduct.product.name}
                            </span>
                        </div>
                        {orderProduct.product.unit && (
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 12px",
                                backgroundColor: token.colorFillQuaternary,
                                borderRadius: token.borderRadius
                            }}>
                                <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>
                                    {t("unit")}
                                </span>
                                <span style={{ fontWeight: 600, color: token.colorText }}>
                                    {orderProduct.product.unit.name}
                                </span>
                            </div>
                        )}
                    </Space>
                </div>

                {/* Store Selector */}
                <div style={{
                    padding: "20px",
                    backgroundColor: token.colorBgContainer,
                    borderRadius: token.borderRadiusLG,
                    border: `1px solid ${token.colorBorder}`,
                    marginBottom: 24,
                }}>
                    <label style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: token.colorText,
                        display: "block",
                        marginBottom: 8
                    }}>
                        {t("store")} <span style={{ color: token.colorError }}>*</span>
                    </label>
                    <StoreSelector
                        value={storeId}
                        onChange={setStoreId}
                        placeholder={t("select_store_to_view_quantities")}
                        disabled={!!(orderProduct?.store_id || orderProduct?.store?.id)}
                    />
                    {(orderProduct?.store_id || orderProduct?.store?.id) && (
                        <div style={{
                            marginTop: 8,
                            padding: "8px 12px",
                            backgroundColor: token.colorInfoBg,
                            borderRadius: token.borderRadius,
                            border: `1px solid ${token.colorInfoBorder}`,
                            color: token.colorInfoText,
                            fontSize: 13
                        }}>
                            {t("store_already_selected_cannot_change")}
                        </div>
                    )}
                </div>

                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <div
                        style={{
                            padding: "20px",
                            backgroundColor: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `1px solid ${token.colorBorder}`,
                        }}
                    >
                        <h4 style={{
                            marginTop: 0,
                            marginBottom: 16,
                            color: token.colorText,
                            fontSize: 16,
                            fontWeight: 600,
                            paddingBottom: 12,
                            borderBottom: `2px solid ${token.colorPrimary}`
                        }}>
                            {t("current_values")}
                        </h4>
                        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                            {(() => {
                                const storeQtyInfo = getStoreQuantityInfo();
                                if (storeQtyInfo) {
                                    return (
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "8px 12px",
                                            backgroundColor: storeQtyInfo.available ? token.colorSuccessBg : token.colorErrorBg,
                                            borderRadius: token.borderRadius,
                                            border: `1px solid ${storeQtyInfo.available ? token.colorSuccessBorder : token.colorErrorBorder}`
                                        }}>
                                            <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>
                                                {t("store_available_quantity")}
                                            </span>
                                            <span style={{ fontSize: 15, fontWeight: 600, color: storeQtyInfo.available ? token.colorSuccess : token.colorError }}>
                                                {storeQtyInfo.available
                                                    ? `${storeQtyInfo.quantity} ${orderProduct.product.unit?.name || ""}`
                                                    : t("unavailable")}
                                            </span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 12px",
                                backgroundColor: token.colorFillQuaternary,
                                borderRadius: token.borderRadius
                            }}>
                                <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>{t("method")}</span>
                                <span style={{ fontSize: 14, fontWeight: 500 }}>
                                    {orderProduct.availableWeight !== undefined && orderProduct.availableWeight !== null
                                        ? t("by_weight")
                                        : t("by_count")}
                                </span>
                            </div>
                            {orderProduct.count !== undefined && orderProduct.count !== null && (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    backgroundColor: token.colorFillQuaternary,
                                    borderRadius: token.borderRadius
                                }}>
                                    <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>{t("count")}</span>
                                    <span style={{ fontSize: 15, fontWeight: 600 }}>{orderProduct.count}</span>
                                </div>
                            )}
                            {orderProduct.unitPartition && (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    backgroundColor: token.colorFillQuaternary,
                                    borderRadius: token.borderRadius
                                }}>
                                    <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>{t("unit_partition")}</span>
                                    <span style={{ fontSize: 14 }}>
                                        {orderProduct.unitPartition.name} ({orderProduct.unitPartition.percentage}%)
                                    </span>
                                </div>
                            )}
                            {orderProduct.availableWeight !== undefined && orderProduct.availableWeight !== null && (
                                <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    backgroundColor: token.colorFillQuaternary,
                                    borderRadius: token.borderRadius
                                }}>
                                    <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>{t("available_weight")}</span>
                                    <span style={{ fontSize: 15, fontWeight: 600 }}>
                                        {orderProduct.availableWeight}
                                    </span>
                                </div>
                            )}
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 12px",
                                backgroundColor: token.colorFillQuaternary,
                                borderRadius: token.borderRadius
                            }}>
                                <span style={{ fontWeight: 500, color: token.colorTextSecondary }}>{t("status")}</span>
                                {orderProduct.isAccepted === false ? (
                                    <span style={{ color: token.colorError, fontWeight: 600, fontSize: 14 }}>
                                        {t("unaccepted")}
                                    </span>
                                ) : (
                                    <span style={{ color: token.colorSuccess, fontWeight: 600, fontSize: 14 }}>
                                        {t("accepted")}
                                    </span>
                                )}
                            </div>
                        </Space>
                    </div>

                    <div
                        style={{
                            padding: "20px",
                            backgroundColor: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `2px solid ${token.colorPrimary}`,
                        }}
                    >
                        {(() => {
                            const hasUnitPartitions = orderProduct.product.unitPartition && orderProduct.product.unitPartition.length > 0;

                            return (
                                <>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        marginBottom: 16,
                                        paddingBottom: 12,
                                        borderBottom: `2px solid ${token.colorPrimary}`
                                    }}>
                                        <h4 style={{
                                            margin: 0,
                                            color: token.colorPrimary,
                                            fontSize: 16,
                                            fontWeight: 600,
                                        }}>
                                            {t("new_values")}
                                        </h4>
                                        {hasUnitPartitions && (
                                            <div style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-end",
                                                gap: 6
                                            }}>
                                                <Switch
                                                    checked={quantityData.method === QuantityInputMethodEnum.BY_WEIGHT}
                                                    onChange={handleMethodChange}
                                                    checkedChildren={<DeploymentUnitOutlined />}
                                                    unCheckedChildren={<NumberOutlined />}
                                                />
                                                <span style={{
                                                    fontSize: 11,
                                                    color: token.colorTextSecondary,
                                                    textAlign: "right",
                                                    maxWidth: 200,
                                                    lineHeight: 1.4
                                                }}>
                                                    {t("quantity_method_helper")}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <Form.Item style={{ marginBottom: 20 }}>
                                        <QuantityInputMethod
                                            value={quantityData}
                                            onChange={setQuantityData}
                                            product={orderProduct.product}
                                        />
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Checkbox
                                            checked={markAsAccepted}
                                            onChange={(e) => setMarkAsAccepted(e.target.checked)}
                                            style={{ fontSize: 14 }}
                                        >
                                            <span style={{ fontWeight: 500 }}>{t("mark_as_accepted")}</span>
                                        </Checkbox>
                                    </Form.Item>
                                </>
                            );
                        })()}
                    </div>
                </Space>
            </Form>
        </Modal>
    );
};

export default EditProductModal;
